'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import ResourceCard from './ResourceCard';
import FilterPanel from './FilterPanel';
import SearchBar from './SearchBar';
import CategoryTabs from './CategoryTabs';
import RecommendedResources from './RecommendedResources';
import CrisisResourcesBanner from './CrisisResourcesBanner';
import ResourceStats from './ResourceStats';

interface Resource {
  id: string;
  title: string;
  description: string;
  category: string;
  format: string;
  image: string;
  alt: string;
  rating: number;
  reviews: number;
  downloads: number;
  gradeLevel: string[];
  urgency: string;
  tags: string[];
}

const ResourceDiscoveryInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [bookmarkedResources, setBookmarkedResources] = useState<string[]>([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeFilters, setActiveFilters] = useState({
    category: 'all',
    format: 'all',
    gradeLevel: 'all',
    urgency: 'all'
  });

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const mockResources: Resource[] = [
  {
    id: '1',
    title: 'College Application Timeline Guide',
    description: 'Comprehensive step-by-step guide covering every stage of the college application process from junior year through acceptance.',
    category: 'College & Career',
    format: 'PDF',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_14958d00f-1766508330962.png",
    alt: 'Open notebook with college planning checklist and colorful sticky notes on wooden desk',
    rating: 4.8,
    reviews: 234,
    downloads: 1567,
    gradeLevel: ['Grade 11', 'Grade 11'],
    urgency: 'Medium',
    tags: ['college', 'applications', 'timeline', 'planning']
  },
  {
    id: '2',
    title: 'Effective Test Preparation Strategies',
    description: 'Proven study techniques and preparation methods to improve test performance and build confidence for exams.',
    category: 'Academic Planning',
    format: 'Video',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_19c5e7dc7-1768252373725.png",
    alt: 'Student studying with organized notes and textbooks in library setting',
    rating: 4.9,
    reviews: 456,
    downloads: 2341,
    gradeLevel: ['Grade 9', 'Grade 10', 'Grade 11'],
    urgency: 'High',
    tags: ['test prep', 'study strategies', 'exams', 'academic']
  },
  {
    id: '3',
    title: 'Career Exploration Interactive Tool',
    description: 'Interactive assessment tool that matches your interests, skills, and values with potential career paths and educational requirements.',
    category: 'College & Career',
    format: 'Interactive',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_190376709-1768145479973.png",
    alt: 'Professional workspace with laptop showing career assessment dashboard and colorful charts',
    rating: 4.7,
    reviews: 189,
    downloads: 987,
    gradeLevel: ['Grade 10', 'Grade 11', 'Grade 11'],
    urgency: 'Low',
    tags: ['career', 'exploration', 'assessment', 'planning']
  },
  {
    id: '4',
    title: 'Academic Goal Setting Workbook',
    description: 'Step-by-step workbook to help students set, track, and achieve their academic goals throughout the school year.',
    category: 'Academic Planning',
    format: 'Article',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1a7939383-1767265721815.png",
    alt: 'Student writing goals in planner with organized desk and educational materials',
    rating: 5.0,
    reviews: 678,
    downloads: 3456,
    gradeLevel: ['Grade 9', 'Grade 10', 'Grade 11'],
    urgency: 'Medium',
    tags: ['goals', 'planning', 'academic', 'organization']
  },
  {
    id: '5',
    title: 'Study Skills Mastery Workshop',
    description: 'Comprehensive workshop covering effective note-taking, time management, memory techniques, and exam preparation strategies.',
    category: 'Academic Planning',
    format: 'Video',
    image: "https://images.unsplash.com/photo-1648757863450-b3a05f6c7529",
    alt: 'Organized study desk with open textbooks, colorful highlighters, and laptop showing study schedule',
    rating: 4.6,
    reviews: 312,
    downloads: 1789,
    gradeLevel: ['Grade 9', 'Grade 10', 'Grade 11'],
    urgency: 'Medium',
    tags: ['study skills', 'academic', 'time management', 'organization']
  },
  {
    id: '6',
    title: 'Financial Aid & Scholarship Guide',
    description: 'Complete guide to understanding financial aid options, FAFSA completion, scholarship search strategies, and application tips.',
    category: 'College & Career',
    format: 'PDF',
    image: "https://images.unsplash.com/photo-1584346881556-19b8804d414f",
    alt: 'Calculator and financial documents with scholarship application forms on desk',
    rating: 4.8,
    reviews: 523,
    downloads: 2876,
    gradeLevel: ['Grade 11', 'Grade 11'],
    urgency: 'High',
    tags: ['financial aid', 'scholarships', 'FAFSA', 'college funding']
  },
  {
    id: '7',
    title: 'Teamwork & Collaboration Skills',
    description: 'Interactive guide on developing effective teamwork, communication, and collaboration skills for group projects and school activities.',
    category: 'Academic Planning',
    format: 'Interactive',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1386a8344-1766873944617.png",
    alt: 'Diverse group of students working together on a school project at library table',
    rating: 4.7,
    reviews: 267,
    downloads: 1432,
    gradeLevel: ['Grade 9', 'Grade 10', 'Grade 11'],
    urgency: 'Low',
    tags: ['teamwork', 'collaboration', 'communication', 'group projects']
  },
  {
    id: '8',
    title: 'Parent Guide: Supporting Your Teen',
    description: 'Comprehensive resource for parents on understanding adolescent development, effective communication, and supporting academic success.',
    category: 'Parent Resources',
    format: 'PDF',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_16cb74b4a-1764834719862.png",
    alt: 'Parent and teenager having positive conversation at kitchen table with open books',
    rating: 4.9,
    reviews: 445,
    downloads: 2134,
    gradeLevel: ['Grade 9', 'Grade 10', 'Grade 11', 'Grade 11'],
    urgency: 'Low',
    tags: ['parents', 'family', 'communication', 'support']
  }];


  const categories = [
  { id: 'all', name: 'All Resources', icon: 'FolderIcon', count: mockResources.length },
  { id: 'College & Career', name: 'College & Career', icon: 'AcademicCapIcon', count: 3 },
  { id: 'Academic Planning', name: 'Academic Planning', icon: 'BookOpenIcon', count: 4 },
  { id: 'Parent Resources', name: 'Parent Resources', icon: 'UserGroupIcon', count: 1 }];


  const filters = {
    categories: ['all', 'College & Career', 'Academic Planning', 'Parent Resources'],
    formats: ['all', 'Video', 'PDF', 'Interactive', 'Article'],
    gradeLevels: ['all', 'Grade 9', 'Grade 10', 'Grade 11'],
    urgencyLevels: ['all', 'High', 'Medium', 'Low']
  };

  const recommendedResources = [
  {
    id: '1',
    title: 'SAT Prep Strategies',
    category: 'Academic Planning',
    image: "https://images.unsplash.com/photo-1518135634229-bbde19020fef",
    alt: 'SAT test preparation materials with pencils and answer sheet on desk',
    reason: 'Based on your Grade 11 profile'
  },
  {
    id: '2',
    title: 'Stress Management Techniques',
    category: 'Mental Health',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_155a316f2-1765132774555.png",
    alt: 'Peaceful yoga mat and meditation cushion in serene room with plants',
    reason: 'Popular among your peers'
  },
  {
    id: '3',
    title: 'Resume Building Workshop',
    category: 'College & Career',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1026f1214-1767550865778.png",
    alt: 'Professional resume template on laptop screen with coffee cup nearby',
    reason: 'Recommended by your counselor'
  }];


  const stats = {
    totalResources: 247,
    totalDownloads: 18456,
    averageRating: 4.7,
    newThisWeek: 12
  };

  const filteredResources = mockResources.filter((resource) => {
    const matchesSearch =
    searchQuery === '' ||
    resource.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    resource.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
    resource.tags.some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesCategory = activeCategory === 'all' || resource.category === activeCategory;
    const matchesFilterCategory = activeFilters.category === 'all' || resource.category === activeFilters.category;
    const matchesFormat = activeFilters.format === 'all' || resource.format === activeFilters.format;
    const matchesGradeLevel =
    activeFilters.gradeLevel === 'all' || resource.gradeLevel.includes(activeFilters.gradeLevel);
    const matchesUrgency = activeFilters.urgency === 'all' || resource.urgency === activeFilters.urgency;

    return matchesSearch && matchesCategory && matchesFilterCategory && matchesFormat && matchesGradeLevel && matchesUrgency;
  });

  const handleBookmark = (id: string) => {
    if (!isHydrated) return;
    setBookmarkedResources((prev) =>
    prev.includes(id) ? prev.filter((resourceId) => resourceId !== id) : [...prev, id]
    );
  };

  const handleViewResource = (id: string) => {
    if (!isHydrated) return;
    console.log('Viewing resource:', id);
  };

  const handleFilterChange = (filterType: string, value: string) => {
    if (!isHydrated) return;
    setActiveFilters((prev) => ({ ...prev, [filterType]: value }));
  };

  const handleClearFilters = () => {
    if (!isHydrated) return;
    setActiveFilters({
      category: 'all',
      format: 'all',
      gradeLevel: 'all',
      urgency: 'all'
    });
    setActiveCategory('all');
  };

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx:264:6" data-component-path="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx" data-component-line="264" data-component-end-line="276" data-component-file="ResourceDiscoveryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%20pt-20%20px-4%20lg%3Apx-6%22%7D" className="min-h-screen bg-background pt-20 px-4 lg:px-6">
        <div data-component-id="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx:265:8" data-component-path="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx" data-component-line="265" data-component-end-line="275" data-component-file="ResourceDiscoveryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20py-8%22%7D" className="max-w-7xl mx-auto py-8">
          <div data-component-id="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx:266:10" data-component-path="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx" data-component-line="266" data-component-end-line="274" data-component-file="ResourceDiscoveryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%20space-y-6%22%7D" className="animate-pulse space-y-6">
            <div data-component-id="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx:267:12" data-component-path="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx" data-component-line="267" data-component-end-line="267" data-component-file="ResourceDiscoveryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-12%20bg-muted%20rounded-lg%20w-1%2F3%22%7D" className="h-12 bg-muted rounded-lg w-1/3" />
            <div data-component-id="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx:268:12" data-component-path="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx" data-component-line="268" data-component-end-line="268" data-component-file="ResourceDiscoveryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-64%20bg-muted%20rounded-xl%22%7D" className="h-64 bg-muted rounded-xl" />
            <div data-component-id="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx:269:12" data-component-path="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx" data-component-line="269" data-component-end-line="273" data-component-file="ResourceDiscoveryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20lg%3Agrid-cols-3%20gap-6%22%7D" className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) =>
              <div data-component-id="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx:271:14" data-component-path="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx" data-component-line="271" data-component-end-line="271" data-component-file="ResourceDiscoveryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-96%20bg-muted%20rounded-xl%22%7D" key={i} className="h-96 bg-muted rounded-xl" />
              )}
            </div>
          </div>
        </div>
      </div>);

  }

  return (
    <div data-component-id="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx:281:4" data-component-path="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx" data-component-line="281" data-component-end-line="354" data-component-file="ResourceDiscoveryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%20pt-20%20px-4%20lg%3Apx-6%22%7D" className="min-h-screen bg-background pt-20 px-4 lg:px-6">
      <div data-component-id="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx:282:6" data-component-path="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx" data-component-line="282" data-component-end-line="353" data-component-file="ResourceDiscoveryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22max-w-7xl%20mx-auto%20py-8%22%7D" className="max-w-7xl mx-auto py-8">
        <div data-component-id="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx:283:8" data-component-path="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx" data-component-line="283" data-component-end-line="290" data-component-file="ResourceDiscoveryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mb-8%22%7D" className="mb-8">
          <h1 data-component-id="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx:284:10" data-component-path="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx" data-component-line="284" data-component-end-line="286" data-component-file="ResourceDiscoveryInteractive.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22text-4xl%20font-heading%20font-bold%20text-foreground%20mb-3%22%2C%22textContent%22%3A%22Resource%20Discovery%20Center%22%7D" className="text-4xl font-heading font-bold text-foreground mb-3">
            Resource Discovery Center
          </h1>
          <p data-component-id="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx:287:10" data-component-path="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx" data-component-line="287" data-component-end-line="289" data-component-file="ResourceDiscoveryInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-lg%20text-muted-foreground%22%2C%22textContent%22%3A%22Explore%20our%20comprehensive%20library%20of%20guidance%20materials%2C%20tools%2C%20and%20support%20resources%22%7D" className="text-lg text-muted-foreground">
            Explore our comprehensive library of guidance materials, tools, and support resources
          </p>
        </div>

        <CrisisResourcesBanner data-component-id="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx:292:8" data-component-path="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx" data-component-line="292" data-component-end-line="292" data-component-file="ResourceDiscoveryInteractive.tsx" data-component-name="CrisisResourcesBanner" data-component-content="%7B%22elementName%22%3A%22CrisisResourcesBanner%22%7D" />
        <ResourceStats data-component-id="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx:293:8" data-component-path="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx" data-component-line="293" data-component-end-line="293" data-component-file="ResourceDiscoveryInteractive.tsx" data-component-name="ResourceStats" data-component-content="%7B%22elementName%22%3A%22ResourceStats%22%7D" stats={stats} />
        <RecommendedResources data-component-id="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx:294:8" data-component-path="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx" data-component-line="294" data-component-end-line="294" data-component-file="ResourceDiscoveryInteractive.tsx" data-component-name="RecommendedResources" data-component-content="%7B%22elementName%22%3A%22RecommendedResources%22%7D" resources={recommendedResources} />

        <CategoryTabs data-component-id="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx:296:8" data-component-path="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx" data-component-line="296" data-component-end-line="299" data-component-file="ResourceDiscoveryInteractive.tsx" data-component-name="CategoryTabs" data-component-content="%7B%22elementName%22%3A%22CategoryTabs%22%7D"
          categories={categories}
          activeCategory={activeCategory}
          onCategoryChange={setActiveCategory} />


        <SearchBar data-component-id="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx:302:8" data-component-path="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx" data-component-line="302" data-component-end-line="306" data-component-file="ResourceDiscoveryInteractive.tsx" data-component-name="SearchBar" data-component-content="%7B%22elementName%22%3A%22SearchBar%22%7D"
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onFilterToggle={() => setIsFilterOpen(!isFilterOpen)}
          resultCount={filteredResources.length} />


        <div data-component-id="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx:309:8" data-component-path="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx" data-component-line="309" data-component-end-line="352" data-component-file="ResourceDiscoveryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20gap-6%22%7D" className="flex gap-6">
          <FilterPanel data-component-id="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx:310:10" data-component-path="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx" data-component-line="310" data-component-end-line="316" data-component-file="ResourceDiscoveryInteractive.tsx" data-component-name="FilterPanel" data-component-content="%7B%22elementName%22%3A%22FilterPanel%22%7D"
            filters={filters}
            activeFilters={activeFilters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            isOpen={isFilterOpen}
            onClose={() => setIsFilterOpen(false)} />


          <div data-component-id="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx:319:10" data-component-path="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx" data-component-line="319" data-component-end-line="351" data-component-file="ResourceDiscoveryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%22%7D" className="flex-1">
            {filteredResources.length === 0 ?
            <div data-component-id="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx:321:12" data-component-path="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx" data-component-line="321" data-component-end-line="337" data-component-file="ResourceDiscoveryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-card%20rounded-xl%20shadow-md%20p-12%20text-center%22%7D" className="bg-card rounded-xl shadow-md p-12 text-center">
                <div data-component-id="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx:322:16" data-component-path="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx" data-component-line="322" data-component-end-line="326" data-component-file="ResourceDiscoveryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20justify-center%20mb-4%22%7D" className="flex justify-center mb-4">
                  <div data-component-id="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx:323:18" data-component-path="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx" data-component-line="323" data-component-end-line="325" data-component-file="ResourceDiscoveryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-4%20bg-muted%20rounded-full%22%7D" className="p-4 bg-muted rounded-full">
                    <Icon data-component-id="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx:324:20" data-component-path="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx" data-component-line="324" data-component-end-line="324" data-component-file="ResourceDiscoveryInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22MagnifyingGlassIcon%22%2C%22className%22%3A%22text-muted-foreground%22%7D" name="MagnifyingGlassIcon" size={48} variant="outline" className="text-muted-foreground" />
                  </div>
                </div>
                <h3 data-component-id="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx:327:16" data-component-path="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx" data-component-line="327" data-component-end-line="327" data-component-file="ResourceDiscoveryInteractive.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-xl%20font-heading%20font-bold%20text-foreground%20mb-2%22%2C%22textContent%22%3A%22No%20Resources%20Found%22%7D" className="text-xl font-heading font-bold text-foreground mb-2">No Resources Found</h3>
                <p data-component-id="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx:328:16" data-component-path="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx" data-component-line="328" data-component-end-line="330" data-component-file="ResourceDiscoveryInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-muted-foreground%20mb-6%22%2C%22textContent%22%3A%22Try%20adjusting%20your%20search%20or%20filters%20to%20find%20what%20you're%20looking%20for%22%7D" className="text-muted-foreground mb-6">
                  Try adjusting your search or filters to find what you're looking for
                </p>
                <button data-component-id="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx:331:16" data-component-path="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx" data-component-line="331" data-component-end-line="336" data-component-file="ResourceDiscoveryInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22px-6%20py-3%20bg-primary%20text-primary-foreground%20rounded-lg%20font-semibold%20hover%3Aopacity-90%20transition-opacity%22%2C%22textContent%22%3A%22Clear%20All%20Filters%22%7D"
                onClick={handleClearFilters}
                className="px-6 py-3 bg-primary text-primary-foreground rounded-lg font-semibold hover:opacity-90 transition-opacity">

                  Clear All Filters
                </button>
              </div> :

            <div data-component-id="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx:339:12" data-component-path="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx" data-component-line="339" data-component-end-line="349" data-component-file="ResourceDiscoveryInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20xl%3Agrid-cols-3%20gap-6%22%7D" className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {filteredResources.map((resource) =>
              <ResourceCard data-component-id="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx:341:14" data-component-path="src/app/resource-discovery-center/components/ResourceDiscoveryInteractive.tsx" data-component-line="341" data-component-end-line="346" data-component-file="ResourceDiscoveryInteractive.tsx" data-component-name="ResourceCard" data-component-content="%7B%22elementName%22%3A%22ResourceCard%22%7D"
                key={resource.id}
                resource={resource}
                onBookmark={handleBookmark}
                onView={handleViewResource}
                isBookmarked={bookmarkedResources.includes(resource.id)} />

              )}
              </div>
            }
          </div>
        </div>
      </div>
    </div>);

};

export default ResourceDiscoveryInteractive;